import { Component, computed, inject } from '@angular/core';
import { TopicStore } from '../../../stores/topic/topic.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { NgClass } from '@angular/common';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import {
  AtlasConfiguration,
  Commune,
  LandingPageItem,
} from '../../../models/commune';
import { Button } from 'primeng/button';
import { Story } from '../../../models/story';
import { Bookmark } from '../../../models/bookmark';
import { ColorToken, Topic } from '../../../models/topic';
import { StoryStore } from '../../../stores/story/story.state';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { DialogModule } from 'primeng/dialog';
import { HeaderComponent } from '../../atoms/header/header.component';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { AtlasIndicatorTreeComponent } from '../atlas-indicator-tree/atlas-indicator-tree.component';
import { IndicatorSelectionListComponent } from '../../molecules/indicator-selection-list/indicator-selection-list.component';
import { CountOverviewComponent } from '../count-overview/count-overview.component';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import {
  TopicColorClasses,
  TopicHelperService,
} from '../../../services/topic/topic-helper.service';
import { PermissionService } from '../../../services/permission.service';
import { getLandingPageItems } from '../../../utility/landing-page.utility';

@Component({
  selector: 'app-atlas-landingpage',
  standalone: true,
  imports: [
    LogoComponent,
    NgClass,
    DividerModule,
    CardModule,
    Button,
    DialogModule,
    HeaderComponent,
    ScrollPanelModule,
    AtlasIndicatorTreeComponent,
    IndicatorSelectionListComponent,
    CountOverviewComponent,
    MenuModule,
  ],
  templateUrl: './atlas-landingpage.component.html',
  styleUrl: './atlas-landingpage.component.scss',
})
export class AtlasLandingpageComponent {
  readonly activeUserStore = inject(ActiveUserStore);
  readonly globalStore = inject(GlobalsStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly topicStore = inject(TopicStore);
  readonly topicHelper = inject(TopicHelperService);
  readonly permissionService = inject(PermissionService);
  combineableIndicators: string[] | null = null;
  config: AtlasConfiguration =
    this.globalStore.communeConfiguration() || this.globalStore.getBaseConfig();

  /**
   * The "Fallback-Thema" ("weitere Bereiche") setting, resolved to a virtual Topic
   * whose bookmark categories are all categories not assigned to any real topic.
   * `null` when hidden or when there's currently nothing unassigned to show.
   */
  fallbackTopic = computed(() =>
    this.topicHelper.getFallbackTopic(this.globalStore.activeCommune())
  );

  /** Whether the landing page is shown in the public atlas or a guest view. */
  isPublicContext = computed(() => {
    const context = this.activeUserStore.context();
    return context === 'atlasExternal' || context === 'guestView';
  });

  /** The landing page items for the current audience (public or internal). */
  get landingPageItems(): LandingPageItem[] {
    return getLandingPageItems(
      this.config,
      this.isPublicContext() ? 'public' : 'internal'
    );
  }

  /**
   * PrimeFlex classes for a topic tile's colored accents (spotlight card
   * background, icon badge) - a solid, theme-independent accent shade of the
   * topic's color, or `null` when it has none (callers fall back to
   * `bg-primary`/`text-primary`). `bgStrong` is the card/badge background,
   * `textStrong` is its icon counterpart (used where the icon sits on a
   * light/inverted background rather than directly on the accent color).
   *
   * @param {ColorToken | undefined} color - The topic's color token, if any.
   * @return {TopicColorClasses | null} The class names, or `null` if there's no valid token.
   */
  getTopicAccentClasses(color: ColorToken | undefined): TopicColorClasses | null {
    return this.topicHelper.getTopicColorClasses(
      color,
      this.globalStore.activeTheme()
    );
  }
  infoVisible = false;
  treeVisible = false;
  indicatorIds: string[] = [];
  menuItems: MenuItem[] = [
    {
      visible: !this.activeUserStore.isLoggedIn(),
      label: 'Login',
      icon: 'fas fa-right-to-bracket',
      target: 'hyperion_login',
      routerLink: ['/login'],
    },
    {
      visible: !!this.activeUserStore.isLoggedIn(),
      label: 'Zur Datenpflege',
      sub: 'Datenpflege in neuem Tab öffnen',
      icon: 'fas fa-database',
      target: 'hyperion_administration',
      routerLink: ['/data-backend'],
    },
    //{
    //  url: 'https://www.youtube.com/playlist?list=PLxyQdUGjPEsF8ZEsi47HHpQRI6-v-8owD',
    //  label: 'Tutorials',
    //  sub: 'YouTube-Kanal in neuem Tab öffnen',
    //  icon: 'fa-brands fa-youtube',
    //},
    {
      visible: !!this.globalStore.activeCommune()?.contactUrl,
      label: 'Kontakt',
      sub: 'Kommunen-Kontakt-Seite in neuem Tab öffnen',
      icon: 'fas fa-at',
      target: 'hyperion_kontakt',
      url: this.globalStore.activeCommune()?.contactUrl,
    },
  ];

  /**
   * Removes the specified indicator ID from the list of selected indicators.
   *
   * @param {string} id - The identifier of the indicator to unselect.
   * @return {void} Does not return a value.
   */
  unselectIndicator(id: string) {
    this.indicatorIds = this.indicatorIds.filter(i => i !== id);
    this.setCombineableIndicatorIds();
  }

  /**
   * Toggles the selection state of an indicator by its identifier. If the indicator is already selected, it will be unselected. Otherwise, it will be marked as selected.
   *
   * @param {string} id - The unique identifier of the indicator to be toggled.
   * @return {void} This method does not return a value.
   */
  toggleIndicatorSelection(id: string) {
    if (this.indicatorIds.includes(id)) {
      this.unselectIndicator(id);
    } else {
      this.indicatorIds.push(id);
      this.setCombineableIndicatorIds();
    }
  }

  /**
   * Triggers the generation of a visual representation for the selected indicators.
   * This method utilizes the global store to emit an event with the relevant indicator IDs.
   *
   * @return {void} Does not return a value.
   */
  visualizeSelectedIndicators() {
    this.globalStore.triggerAtlasEvent('generateView', {
      indicatorIds: this.indicatorIds || [],
    });
  }

  /**
   * Enhances a landing page item with additional properties based on its type.
   *
   * @param {LandingPageItem} landingPageItem - The landing page item to enhance.
   * This object must have a valid `type` to determine how the enhancement is applied.
   * @return {LandingPageItem | null} A new landing page item object with enhanced
   * properties or null if the enhancement could not be applied due to an invalid type or missing entityObject.
   */
  enhancedLandingPageItem(
    landingPageItem: LandingPageItem
  ): LandingPageItem | null {
    const entityObject = this.getEntityObject(landingPageItem);

    if (!entityObject) return null;

    switch (landingPageItem.type) {
      case 'Topic':
        if (!this.topicHelper.isTopicVisibleForContext(entityObject as Topic)) {
          return null;
        }
        // the public atlas (anonymous, no permissions) only receives topics
        // the server already scoped to published and non-shy ones
        if (
          !this.isPublicContext() &&
          !this.permissionService.checkThemeRights(
            entityObject._id,
            'read',
            false
          )
        ) {
          return null;
        }
        return {
          ...landingPageItem,
          ...{
            name: entityObject.title,
            text: entityObject.description,
            icon: `fas fa-${entityObject.icon}`,
            spotlight: entityObject.spotlight,
            color: entityObject.color,
          },
        };
      case 'Bookmark':
        return {
          ...landingPageItem,
          ...{
            name: entityObject.title,
            text: entityObject.headline,
            icon: 'fas fa-note-sticky',
          },
        };
      case 'Story':
        return {
          ...landingPageItem,
          ...{
            name: entityObject.title,
            text: entityObject.description,
            icon: 'fas fa-file-lines',
          },
        };
      default:
        return null;
    }
  }

  /**
   * Retrieves the appropriate entity object based on the type specified in the item.
   *
   * @param {LandingPageItem} item - The item containing the type and ID used to fetch the entity object.
   * @return {Bookmark|Topic|Story|null} Returns the corresponding entity object (Bookmark, Topic, or Story) or null if the type is unrecognized.
   */
  getEntityObject(item: LandingPageItem): Bookmark | Topic | Story | null {
    switch (item.type) {
      case 'Topic':
        return this.topicStore.getById(item.id);
      case 'Bookmark':
        return this.bookmarkStore.getById(item.id);
      case 'Story':
        return this.storyStore.getById(item.id);
      default:
        return null;
    }
  }

  /**
   * Handles the call-to-action (CTA) for a landing page item based on its type.
   *
   * @param {LandingPageItem} item - The landing page item to handle. Contains details such as type and identifier.
   * @return {void} Does not return any value. Executes corresponding actions based on the item type.
   */
  landingPageItemCTA(item: LandingPageItem): void {
    switch (item.type) {
      case 'Bookmark':
        this.closeLandingPage();
        this.globalStore.triggerAtlasEvent('loadBookmark', {
          bookmarkId: item.id,
        });
        break;
      case 'Topic':
        this.globalStore.triggerAtlasEvent('loadTopic', {
          topic: this.topicStore.getById(item.id),
        });

        this.closeLandingPage();

        break;
      case 'Story':
        this.closeLandingPage();
        this.globalStore.triggerAtlasEvent('loadStory', {
          storyId: item.id,
        });
        break;
    }
  }

  /**
   * Returns a label based on the provided type.
   *
   * @param {string} type The type for which the label will be returned. Valid types are 'Bookmark', 'Topic', and 'Story'.
   * @return {string} The label corresponding to the given type. Returns an empty string if the type is not recognized.
   */
  getLabelByType(type: string) {
    switch (type) {
      case 'Bookmark':
        return 'zur Ansicht';
      case 'Topic':
        return 'Thema aufrufen';
      case 'Story':
        return 'zum Bericht';
      default:
        return '';
    }
  }

  /**
   * Invokes the search functionality by triggering an Atlas event named 'openSearch'.
   *
   * @return {void} Does not return a value.
   */
  callSearch(): void {
    this.globalStore.triggerAtlasEvent('openSearch', {});
  }

  /**
   * Invokes the search functionality by triggering an Atlas event named 'openSearch'.
   *
   * @return {void} Does not return a value.
   */
  callStartPage(): void {
    this.globalStore.triggerAtlasEvent('callStartPage', {});
  }

  /**
   * Opens the "Fallback-Thema" ("weitere Bereiche"), the virtual topic covering all
   * bookmark categories not assigned to any real topic.
   *
   * @return {void} Does not return a value.
   */
  openFallbackTopic(): void {
    const topic = this.fallbackTopic();
    if (!topic) return;
    this.globalStore.triggerAtlasEvent('loadTopic', { topic });
    this.closeLandingPage();
  }

  /**
   * Closes the landing page by triggering the 'closeLandingPage' event.
   * This method uses a delayed timeout to perform the action.
   *
   * @return {void} Does not return a value.
   */
  closeLandingPage(): void {
    setTimeout(() => {
      this.globalStore.triggerAtlasEvent('closeLandingPage', {});
    });
  }

  /**
   * Checks if a default bookmark exists for the active user's context within the active commune.
   *
   * @return {boolean} Returns `true` if a default bookmark exists, otherwise `false`.
   */
  checkDefaultBookmark(): boolean {
    const commune = this.globalStore.activeCommune();
    if (!commune) return false;

    const context = this.activeUserStore.context();
    return !!this.getBookmarkByContext(context, commune);
  }

  /**
   * Retrieves a bookmark based on the provided context and commune.
   *
   * @param {string} context - The context to determine which bookmark to retrieve, e.g., 'atlasInternal'.
   * @param {Commune} commune - The commune object containing bookmark identifiers.
   * @return {Bookmark | null} The bookmark corresponding to the given context, or null if it cannot be found.
   */
  private getBookmarkByContext(
    context: string,
    commune: Commune
  ): Bookmark | null {
    const defaultBookmarkId =
      context === 'atlasInternal'
        ? commune.defaultBookmark
        : commune.defaultBookmarkExternal;

    return this.bookmarkStore.getById(defaultBookmarkId);
  }

  setCombineableIndicatorIds() {
    this.combineableIndicators =
      this.indicatorIds.length > 0
        ? this.dataStore.getIndicatorsWithLayerOverlap(this.indicatorIds)
        : null;
  }

  showInfo() {
    this.infoVisible = true;
  }

  showTree() {
    this.treeVisible = true;
  }
}
